import { ExpressedOutcomeEnum, SideEnum } from '@talos/kyoko';
import {
  getPredictionOutcomePrice,
  type PredictionOutcomeQuotes,
} from '../../../PredictionMarkets/PredictionCard/predictionMath';
import {
  PredictionMarketsPriceView,
  type PredictionMarketsPriceGridFilterState,
  type PredictionMarketsPriceView as PredictionMarketsPriceViewType,
} from './types';

export const PredictionMarketsPriceBookSide = {
  Bid: 'bid',
  Offer: 'offer',
} as const;
export type PredictionMarketsPriceBookSide =
  (typeof PredictionMarketsPriceBookSide)[keyof typeof PredictionMarketsPriceBookSide];

export interface PredictionMarketsPriceSideDescriptor {
  readonly header: string;
  readonly outcome: ExpressedOutcomeEnum;
  readonly bookSide: PredictionMarketsPriceBookSide;
}

const BOTH_PRICE_SIDES: readonly [PredictionMarketsPriceSideDescriptor, PredictionMarketsPriceSideDescriptor] = [
  {
    header: 'Yes',
    outcome: ExpressedOutcomeEnum.Yes,
    bookSide: PredictionMarketsPriceBookSide.Offer,
  },
  {
    header: 'No',
    outcome: ExpressedOutcomeEnum.No,
    bookSide: PredictionMarketsPriceBookSide.Offer,
  },
];

const YES_PRICE_SIDES: readonly [PredictionMarketsPriceSideDescriptor, PredictionMarketsPriceSideDescriptor] = [
  {
    header: 'Bid',
    outcome: ExpressedOutcomeEnum.Yes,
    bookSide: PredictionMarketsPriceBookSide.Bid,
  },
  {
    header: 'Offer',
    outcome: ExpressedOutcomeEnum.Yes,
    bookSide: PredictionMarketsPriceBookSide.Offer,
  },
];

const NO_PRICE_SIDES: readonly [PredictionMarketsPriceSideDescriptor, PredictionMarketsPriceSideDescriptor] = [
  {
    header: 'Bid',
    outcome: ExpressedOutcomeEnum.No,
    bookSide: PredictionMarketsPriceBookSide.Bid,
  },
  {
    header: 'Offer',
    outcome: ExpressedOutcomeEnum.No,
    bookSide: PredictionMarketsPriceBookSide.Offer,
  },
];

export function getPredictionMarketsPriceView(
  filterState: PredictionMarketsPriceGridFilterState
): PredictionMarketsPriceViewType {
  const { priceView } = filterState;
  if (priceView === PredictionMarketsPriceView.Yes || priceView === PredictionMarketsPriceView.No) {
    return priceView;
  }
  return PredictionMarketsPriceView.Both;
}

export function setPredictionMarketsPriceView(
  filterState: PredictionMarketsPriceGridFilterState,
  priceView: PredictionMarketsPriceViewType
): PredictionMarketsPriceGridFilterState {
  if (priceView === PredictionMarketsPriceView.Both) {
    const { priceView: _omitted, ...rest } = filterState;
    return rest;
  }

  return {
    ...filterState,
    priceView,
  };
}

export function getPredictionMarketsPriceSides(
  priceView: PredictionMarketsPriceViewType
): readonly [PredictionMarketsPriceSideDescriptor, PredictionMarketsPriceSideDescriptor] {
  switch (priceView) {
    case PredictionMarketsPriceView.Both:
      return BOTH_PRICE_SIDES;
    case PredictionMarketsPriceView.Yes:
      return YES_PRICE_SIDES;
    case PredictionMarketsPriceView.No:
      return NO_PRICE_SIDES;
    default: {
      const unexpectedPriceView: never = priceView;
      throw new Error(`Unsupported prediction markets price view: ${unexpectedPriceView}`);
    }
  }
}

function toExecutableSide(bookSide: PredictionMarketsPriceBookSide): SideEnum {
  switch (bookSide) {
    case PredictionMarketsPriceBookSide.Bid:
      return SideEnum.Sell;
    case PredictionMarketsPriceBookSide.Offer:
      return SideEnum.Buy;
    default: {
      const unexpectedBookSide: never = bookSide;
      throw new Error(`Unsupported prediction markets price book side: ${unexpectedBookSide}`);
    }
  }
}

export function getPredictionMarketSidePrice(
  side: PredictionMarketsPriceSideDescriptor,
  quotes: PredictionOutcomeQuotes
): string | undefined {
  return getPredictionOutcomePrice(side.outcome, toExecutableSide(side.bookSide), quotes);
}
